import classNames from 'classnames';
import React from 'react';
import _ from 'lodash';
import cls from './form-control.module.scss';
import {Card} from "primereact/card";
import DisplayRendering from "../DisplayRendering";
import {
  errMsgReplace,
  fieldDefaultConfig,
  isDisplayStatus,
  isFieldId,
  isRecId,
  renderValue,
  toBool,
  wrapArray
} from "../utils";
import {Button} from "primereact/button";

export type FormControlProps = {
  name: string;
  className?: string;
  card?: boolean;
  display_status?: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  helperText?: React.ReactNode;
  helperTextWrapperClassName?: string;
  errorMessage?: React.ReactNode;
  labelWrapperClassName?: string;
  descriptionWrapperClassName?: string;
  errorWrapperClassName?: string;
  children?: React.ReactNode;
  rest?: any;

};

export const FormControl = ({
  className,
  name = '',
  card = false,
  display_status = 'optional',
  errorMessage,
  label,
  description,
  children,
  labelWrapperClassName,
  descriptionWrapperClassName,
  errorWrapperClassName,
  helperText,
  helperTextWrapperClassName,
    rest
}: FormControlProps) => {
  const {extraConfig, details} = rest

  const {field, record_id, params = {} as any} = details
  const isCreate = !isRecId(record_id)
  const isEdit = !isCreate
  console.log('Starting FormControl', field.name, {errorMessage,field, extraConfig})
  const {display:displayConfig = {}, deleted = false} = extraConfig
  const defs = fieldDefaultConfig( 'form', field.type, field.options, extraConfig )
  const displayMode = wrapArray(displayConfig.mode || defs.display_mode, true)
  console.log('FormControl', field, defs.display_mode, displayMode, isCreate, isEdit)

  const pCheck = (type, def = false) => {
    if(toBool(_.get(params, `form_${type}`)))return true
    if(field.key && toBool(_.get(params, `${type}_${field.key}`)))return true
    const only = wrapArray( _.get(params,`form_${type}_only`, ''), true )
    if ( only.length ) return only.includes(field.id) || only.includes(field.key) || only.includes(field.name)

    const exclude = wrapArray( _.get(params,`form_${type}_exclude`, ''), true )
    if(exclude.length) return !( exclude.includes(field.id) || exclude.includes(field.key) || exclude.includes(field.name))
    return def
  }
  let display = true
  if(!pCheck('show',true))display = false
  if(display && (displayMode.includes('hide')
      || (displayMode.includes('hide_create') && isCreate)
      || (displayMode.includes('hide_edit') && isEdit)
      || pCheck('hide') || isDisplayStatus(display_status,'hidden')))display = false
  if(display && (displayMode.includes('display')
      || (displayMode.includes('display_create') && isCreate)
      || (displayMode.includes('display_edit') && isEdit)
      || pCheck('display') || isDisplayStatus(display_status,'display'))){
    if(isFieldId(field.id)) {
      extraConfig.always_display = true;
      children = <DisplayRendering name={name} className={className} {...rest}/>
    }else{
      console.log('Do not display this field:', field.name);
    }
  }
  if(display && ( (!displayMode.length || displayMode.includes('edit')
          || (displayMode.includes('edit_create') && isCreate)
          || (displayMode.includes('edit_edit') && isEdit) || displayMode.includes('display'))
      && pCheck('edit',true) &&  isDisplayStatus(display_status,['optional','require','disable','display']))){
      //do inner
  }else{
    console.log('Skipping Field', label, display,  displayMode, pCheck('edit',true), display_status)
    display = false
  }
  const checkDeleted = deleted ? <Button className={'p-button-rounded p-button-danger mr-1'} style={{display: 'absolute', left: 0}} icon={'pi pi-exclamation-triangle'} tooltip={'Warning: Field No Longer Active. Please Contact Form Admin to Remove.'}/> : ''
  const useLabel = <>{checkDeleted}{label ? label + (isDisplayStatus(display_status,'require') ?  ' *' : '') : ''}</>
  const inner = (
      <>
      {children}
      {helperText && (
          <div id={`${name}-help`} className={classNames('block', card ? 'p-card-subtitle' : cls.subTitle, helperTextWrapperClassName)}>
            {renderValue(helperText, {render_as: 'markdown'})}
          </div>
      )}
      {errorMessage && (
          <div id={`${name}-error`} className={classNames('block','p-error', errorWrapperClassName)}>
            {errMsgReplace(errorMessage, field, label, name)}
          </div>
      )}

      </>


)
  const control = (
      card ? (
          <Card title={useLabel} subTitle={description} style={{marginBottom: '10px'}}>
            <div className={classNames('field',className)}>
              {inner}
            </div>
          </Card>

      ) : (
          <div className={classNames('field',className)}>
            {(label || description) && (
                <label htmlFor={name}>
                  {label && <div className={classNames('block',cls.title, labelWrapperClassName)}>{useLabel}</div>}
                  {description && <div className={classNames('block',cls.subTitle, descriptionWrapperClassName)}>{renderValue(description, {render_as: 'markdown'})}</div>}
                </label>
            )}
            {inner}
          </div>
      )
  );
  return display ? control : <div className={'hidden'}>{control}</div>
}

export type WithFormControlProps = Omit<FormControlProps, 'className'> & {
  controlWrapperClassName?: string;
};

// eslint-disable-next-line
export type  FormControlComponentDefaultProps<T = any> = {
  className?: string;
  name?: string;
  onChange?: (e?:any) => void;
  onBlur?: (e?:any) => void;
  value?: T;
};

export function withFormControl<T extends FormControlComponentDefaultProps>(Component: React.ComponentType<T>) {
  // eslint-disable-next-line
  return function ComponentWithFormControl({
    name,
    className,
    display_status,
    card,
    controlWrapperClassName,
    label,
    labelWrapperClassName,
    errorMessage,
    errorWrapperClassName,
    description,
    descriptionWrapperClassName,
    helperText,
    helperTextWrapperClassName,
    ...restProps
  }: T & WithFormControlProps) {

    return (
      <FormControl
        name={name}
        className={controlWrapperClassName}
        label={label}
        card={card}
        display_status={display_status}
        labelWrapperClassName={labelWrapperClassName}
        description={description}
        descriptionWrapperClassName={descriptionWrapperClassName}
        errorMessage={errorMessage}
        errorWrapperClassName={errorWrapperClassName}
        helperText={helperText}
        helperTextWrapperClassName={helperTextWrapperClassName}
        rest={restProps}
      >
        <Component name={name} className={classNames(className, errorMessage ? 'invalid' : '')} {...(restProps as T)} />
      </FormControl>
    );
  };
}

export default FormControl;
